import React, { useContext, useEffect, useState } from 'react'
import { Checkbox, Table, TableBody, TableCell, TableContainer} from '@mui/material'
import EnhancedTableHead from './EnhancedTableHead';
import { IOperationFilter, OperationsFieldOrderBy } from '../../../Store/Operations/types';
import { FieldsOrder, ISubjectsFilter, SubjectsFieldOrderBy } from '../../../Store/Subjects/types';
import { useSnackbar } from 'notistack';
import { AccountModel, AdjudicationPending, MultimodalSubject, Operation, SubjectModality } from 'neurotec-megamatcherid-management-client';
import DataTableCell from './DataTableCell';
import DataTableRow from './DataTableRow';
import PermIdentityIcon from '@mui/icons-material/PermIdentity';
import GraphicEqIcon from '@mui/icons-material/GraphicEq';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { handleReduceLivenessMode, handleReduceValue } from '../../../helpers/dataTableHelper';
import { AccountsFieldOrderBy, IAccountFilter } from '../../../Store/Accounts/types';
import StyledTooltip from '../StyledTooltip';
import DataTableCellButton from './DataTableCellButton';
import { AdjudicationPendingFieldOrderBy, AdjudicationSolvedFieldOrderBy, AdjudicatorDecisionFieldOrderBy, IAdjudicationPendingFilter, IAdjudicationSolvedFilter, IAdjudicatorDecisionFilter } from '../../../Store/Adjudication/types';
import { AdjudicationSolved, AdjudicatorDecision } from 'neurotec-megamatcherid-management-client-adjudicator';
import { NavigationContext } from '../../../Store/Navigation/context';

export type Order = 'asc' | 'desc';
export type OrderByKey =  "NONE" | 'id' | 'SUBJECT_ID' | 'SUBJECT_LIVENESS_SCORE' | 'SUBJECT_QUALITY' 
        | 'SUBJECT_STATUS' | 'SUBJECT_AGE' | 'SUBJECT_DATE' | 'SUBJECT_LIVENESS_MODE' | 'OPERATION_OPERATION_TYPE' | 'UUID'
        | 'OPERATION_DATE' | 'OPERATION_STATUS' | 'OPERATION_LIVENESS_MODE' | 'OPERATION_LIVENESS_SCORE' | 'OPERATION_AGE'
        | 'OPERATION_QUALITY' | 'EXCEPTION_UUID' | 'EXCEPTION_DATE' | 'EXCEPTION_HTTP_RESPONSE_CODE' | 'EXCEPTION_NAME' | 'EXCEPTION_MESSAGE' | 'EXCEPTION_PATH' | "OPERATION_MODALITY" | "SUBJECT_MODALITY"
        | "USERNAME" | "ROLES" | "NONE_CHANGE" | "NONE_DELETE" | "FILTER_ADJUDICATION_PENDING_UUID" | "FILTER_ADJUDICATION_PENDING_OPERATION_UUID" | "FILTER_ADJUDICATION_PENDING_DATE" | "FILTER_ADJUDICATOR_DECISION_DATE"
        | "FILTER_ADJUDICATOR_DECISION_ADJUDICATION_UUID" | "FILTER_ADJUDICATOR_DECISION_DECISION" | "FILTER_ADJUDICATOR_DECISION_TYPE_OF_ATTACK" | "FILTER_ADJUDICATION_PENDING_ISSUES" | "FILTER_ADJUDICATION_SOLVED_DECISION_DATE"
        | "FILTER_ADJUDICATION_SOLVED_ISSUES" | "FILTER_ADJUDICATION_SOLVED_FINAL_DECISION";

export interface HeadCell {
    disablePadding: boolean;
    id: OrderByKey;
    label: string;
    align: 'left' | 'right';
    width?: string;
    identifier?: string;
    type?: string 
}

export interface IDataTableId {
    id: number,
    uuid: string
}

interface IDataTableProps {
    data: MultimodalSubject[] | Operation[] | AccountModel[] | AdjudicationPending[] | AdjudicationSolved[],
    filter: IOperationFilter | ISubjectsFilter | IAccountFilter,
    onFilterChange: (filter: any) => void,
    onRowClick?: (row: any) => void,
    headCells: HeadCell[],
    title: string,
    dateName?: string,
    setSelected?: React.Dispatch<React.SetStateAction<IDataTableId[]>>,
    selected?: IDataTableId[],
    onDelete?: (id: IDataTableId) => void
    onChange?: (id: IDataTableId) => void
    type: "subjects" | "operations-face" | "operations-fingers" | "operations-voice" | "accounts" | "adjudication-pending" | "adjudicator-decision" | "adjudication-solved"
}

const DataTable: React.FC<IDataTableProps> = (props) => {

    const { enqueueSnackbar } = useSnackbar();
    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<OrderByKey>(props.type === "subjects"? "SUBJECT_ID" : "OPERATION_DATE");
    const navigationState = useContext(NavigationContext).state

    useEffect(() => {
        setOrder(props.filter.fieldsOrder === FieldsOrder.DESC ? 'desc' : 'asc')
        if ('subjectsFieldToOrderBy' in props.filter && props.filter.subjectsFieldToOrderBy) {
            setOrderBy(props.filter.subjectsFieldToOrderBy)
        } else if ('operationsFieldToOrderBy' in props.filter && props.filter.operationsFieldToOrderBy) {
            setOrderBy(props.filter.operationsFieldToOrderBy)
        } else if ('accountFieldToOrderBy' in props.filter && props.filter.accountFieldToOrderBy) {
            setOrderBy(props.filter.accountFieldToOrderBy)
        } else {
            setOrderBy(props.type === "subjects"? "SUBJECT_ID" : "OPERATION_DATE")
        }
    }, [props.filter, props.type])

    const handleRowBackgroundColor = (index: number): string => {
        return index % 2 === 0? "#fefefe" : "#fafafa"
    }

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (props.setSelected) {
            if (event.target.checked) {
                let newSelecteds: IDataTableId[] 
                if (props.type === "subjects" || props.type === "operations-face" || props.type === "operations-fingers" || props.type === "operations-voice") {
                    newSelecteds = props.data.map((n: { id?: any; uuid?: any; }) => {return {id: n.id, uuid: n.uuid}});
                } else {
                    newSelecteds = props.data.map((n: { id?: any; username?: any; }) => {return {id: n.id ?? 0, uuid: n.username}});
                }
                props.setSelected(newSelecteds);
                return;
            }
            props.setSelected([]);
        }
    };

    const handleRequestSort = (event: React.MouseEvent<unknown>, property: OrderByKey) => {
        const nextOrder: Order = orderBy === property && order === 'asc' ? 'desc' : 'asc';
        const isAsc = nextOrder === 'asc';
        setOrder(nextOrder);
        setOrderBy(property);

        switch (props.type) {
            case "subjects":
                let subjectFilter: ISubjectsFilter = {
                    ...props.filter,
                    subjectsFieldToOrderBy: SubjectsFieldOrderBy[property.toString() as keyof typeof SubjectsFieldOrderBy],
                    fieldsOrder: isAsc? FieldsOrder.ASC : FieldsOrder.DESC
                }
                props.onFilterChange(subjectFilter)
                break;
            case "operations-fingers":
            case 'operations-voice':
            case "operations-face":
                let operationFilter: IOperationFilter = {
                    ...props.filter as IOperationFilter,
                    operationsFieldToOrderBy: OperationsFieldOrderBy[property.toString() as keyof typeof OperationsFieldOrderBy],
                    fieldsOrder: isAsc? FieldsOrder.ASC : FieldsOrder.DESC
                }
                props.onFilterChange(operationFilter)
                break;
            case "accounts":
                let accountFilter: IAccountFilter = {
                    ...props.filter,
                    accountFieldToOrderBy: AccountsFieldOrderBy[property.toString() as keyof typeof AccountsFieldOrderBy],
                    fieldsOrder: isAsc? FieldsOrder.ASC : FieldsOrder.DESC
                }
                props.onFilterChange(accountFilter)
                break;
            case "adjudication-pending":
                let adjudicationPendingFilter: IAdjudicationPendingFilter = {
                    ...props.filter,
                    fieldToOrderBy: AdjudicationPendingFieldOrderBy[property.toString() as keyof typeof AdjudicationPendingFieldOrderBy],
                    fieldsOrder: isAsc? FieldsOrder.ASC : FieldsOrder.DESC
                }
                props.onFilterChange(adjudicationPendingFilter)
                break;
            case "adjudicator-decision":
                let adjudicatorDecisionFilter: IAdjudicatorDecisionFilter = {
                    ...props.filter,
                    fieldToOrderBy: AdjudicatorDecisionFieldOrderBy[property.toString() as keyof typeof AdjudicatorDecisionFieldOrderBy],
                    fieldsOrder: isAsc? FieldsOrder.ASC : FieldsOrder.DESC
                }
                props.onFilterChange(adjudicatorDecisionFilter)
                break;
            case "adjudication-solved":
                let adjudicationSolvedFilter: IAdjudicationSolvedFilter = {
                    ...props.filter,
                    fieldToOrderBy: AdjudicationSolvedFieldOrderBy[property.toString() as keyof typeof AdjudicationSolvedFieldOrderBy],
                    fieldsOrder: isAsc? FieldsOrder.ASC : FieldsOrder.DESC
                }
                props.onFilterChange(adjudicationSolvedFilter)
                break;
            default:
                enqueueSnackbar("Data table type not correct", {variant: "error"})
                break;
        }
    };

    const handleClick = (event: React.MouseEvent<unknown> | undefined, id: number, uuid: string) => {
        if (props.selected && props.setSelected) {
            let selectedIndex: number;
            if (id !== 0) {
                selectedIndex = props.selected.findIndex(v => v.id === id)
            } else {
                selectedIndex = props.selected.findIndex(v => v.uuid === uuid)
            }
            let newSelected: IDataTableId[] = [];
    
            if (selectedIndex === -1) {
                newSelected = newSelected.concat(props.selected, {id: id, uuid: uuid});
            } else if (selectedIndex === 0) {
                newSelected = newSelected.concat(props.selected.slice(1));
            } else if (selectedIndex === props.selected.length - 1) {
                newSelected = newSelected.concat(props.selected.slice(0, -1));
            } else if (selectedIndex > 0) {
                newSelected = newSelected.concat(
                    props.selected.slice(0, selectedIndex),
                    props.selected.slice(selectedIndex + 1),
                );
            }
            props.setSelected(newSelected);
        }
    };

    const isSelected = (findEl: number | string): boolean => {
        if (props.selected) {
            if (typeof findEl === "number") {
                return props.selected.findIndex(v => v.id === findEl) !== -1
            } else {
                return props.selected.findIndex(v => v.uuid === findEl) !== -1
            }
        }
        return false
    }

    const getModalityCell = (modality: SubjectModality | undefined, key?: string) => {
        switch (modality) {
            case "FACE_MODALITY":
                return (
                    <StyledTooltip title="Face" key={key ?? undefined}>
                        <PermIdentityIcon />
                    </StyledTooltip>
                )
            case "VOICE_MODALITY":
                return (
                    <StyledTooltip title="Voice" key={key ?? undefined}>
                        <GraphicEqIcon/>
                    </StyledTooltip>
                )
            case "FINGERS_MODALITY":
                return (
                    <StyledTooltip title="Fingers" key={key ?? undefined}>
                        <FingerprintIcon/>
                    </StyledTooltip>
                )
            default:
                return (
                    <StyledTooltip title="Unknown" key={key ?? undefined}>
                        <QuestionMarkIcon/>
                    </StyledTooltip>
                )
        }
    }

    const showSubjectData = () => (
        <>
        {props.data.map((row: MultimodalSubject, index: number) => {
            const isItemSelected = isSelected(Number(row.id));
            const labelId = `enhanced-table-checkbox-${index}`;
            return (
                <DataTableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.id}
                    selected={isItemSelected}
                    style={{backgroundColor: handleRowBackgroundColor(index)}}
                >
                    <TableCell padding="checkbox">
                        <Checkbox
                            onChange={(event: any) => handleClick(event, Number(row.id), "")}
                            checked={isItemSelected}
                            inputProps={{
                                'aria-labelledby': labelId,
                            }}
                        />
                    </TableCell>
                    <TableCell onClick={() => props.onRowClick?.(row)} padding="checkbox">
                        {row.multimodalOperations?.map((o, index) => 
                            getModalityCell(o.modality? o.modality : undefined, row.id?.toString() + "_" + index.toString())
                        )}
                    </TableCell>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} type="string" align={"left"} tooltipValue={row.subjectId}
                        value={row.subjectId}
                    />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} type="date" align={"left"}
                        value={handleReduceValue(row.multimodalOperations?.map(o => o.date))} 
                    />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} type="number" align={"left"} 
                        value={handleReduceValue(row.multimodalOperations?.map(o => o.age))}
                    />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} type="number" align={"left"}
                        value={handleReduceValue(row.multimodalOperations?.map(o => o.quality))}
                    />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} type="liveness" align={"left"}
                        value={handleReduceLivenessMode(row.multimodalOperations?.map(o => o.livenessMode))}
                    />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} type="number" align={"left"}
                        value={handleReduceValue(row.multimodalOperations?.map(o => o.livenessScore))}
                    />
                </DataTableRow>
            )
        })}
        </>
    )

    const showOperationFaceData = () => (
        <>
        {props.data.map((row: Operation, index: number) => {
            const isItemSelected = isSelected(row.uuid ?? "");
            const labelId = `enhanced-table-checkbox-${index}`;
            return (
                <DataTableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.uuid}
                    selected={isItemSelected ?? false}
                    style={{backgroundColor: handleRowBackgroundColor(index), opacity: navigationState.isLoading ? "50%" : "100%"}}
                >
                    <TableCell padding="checkbox">
                        <Checkbox
                            onChange={(event: any) => handleClick(event, 0, row.uuid ?? "")}
                            checked={isItemSelected ?? false}
                            inputProps={{
                                'aria-labelledby': labelId,
                            }}
                        />
                    </TableCell>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.date} type="date" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.status} type="status" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.operationType} type="type" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.livenessMode} type="liveness" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.livenessScore} type="number" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.age} type="number" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.quality} type="number" align={"left"} />
                </DataTableRow>
            )
        })}
        </>
    )

    const showOperationFingerData = () => (
        <>
        {props.data.map((row: Operation, index: number) => {
            const isItemSelected = isSelected(row.uuid ?? "");
            const labelId = `enhanced-table-checkbox-${index}`;
            return (
                <DataTableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.uuid}
                    selected={isItemSelected ?? false}
                    style={{backgroundColor: handleRowBackgroundColor(index), opacity: navigationState.isLoading ? "50%" : "100%"}}
                >
                    <TableCell padding="checkbox">
                        <Checkbox
                            onChange={(event: any) => handleClick(event, 0, row.uuid ?? "")}
                            checked={isItemSelected ?? false}
                            inputProps={{
                                'aria-labelledby': labelId,
                            }}
                        />
                    </TableCell>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.date} type="date" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.status} type="status" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.operationType} type="type" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.quality} type="number" align={"left"} />
                </DataTableRow>
            )
        })}
        </>
    )

    const showOperationVoiceData = () => (
        <>
        {props.data.map((row: Operation, index: number) => {
            const isItemSelected = isSelected(row.uuid ?? "");
            const labelId = `enhanced-table-checkbox-${index}`;
            return (
                <DataTableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.uuid}
                    selected={isItemSelected ?? false}
                    style={{backgroundColor: handleRowBackgroundColor(index), opacity: navigationState.isLoading ? "50%" : "100%"}}
                >
                    <TableCell padding="checkbox">
                        <Checkbox
                            onChange={(event: any) => handleClick(event, 0, row.uuid ?? "")}
                            checked={isItemSelected ?? false}
                            inputProps={{
                                'aria-labelledby': labelId,
                            }}
                        />
                    </TableCell>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.date} type="date" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.status} type="status" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.operationType} type="type" align={"left"} />
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.quality} type="number" align={"left"} />
                </DataTableRow>
            )
        })}
        </>
    )

    const showAccountData = () => (
        <>
        {props.data.map((row: AccountModel, index: number) => {
            const isItemSelected = isSelected(row.username ?? "");
            return (
                <DataTableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.username}
                    selected={isItemSelected ?? false}
                    style={{backgroundColor: handleRowBackgroundColor(index)}}
                    disablePointer
                >
                    <DataTableCell value={row.username} type="string" align={"left"} tooltipValue={row.username}/>
                    <DataTableCell value={row.role} type="string" align={"left"} />
                    <DataTableCell value={row.uuid} type="longstring" align={"left"} />
                    <DataTableCellButton title="Change" onClick={() => props.onChange?.({id: 0, uuid: row.username ?? ""})} />
                    <DataTableCellButton title="Delete" onClick={() => props.onDelete?.({id: 0, uuid: row.username ?? ""})} color={"error"}/>
                </DataTableRow>
            )
        })}
        </>
    )

    const showAdjudicationPendingData = () => (
        <>
        {props.data.map((row: AdjudicationPending, index: number) => {
            const isItemSelected = isSelected(row.uuid ?? "");
            return (
                <DataTableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.uuid}
                    selected={isItemSelected ?? false}
                    style={{backgroundColor: handleRowBackgroundColor(index)}}
                >
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.date} type="date" align={"left"}/>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.uuid} type="longstring" align={"left"}/>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.issues} type="issue" align={"left"}/>
                </DataTableRow>
            )
        })}
        </>
    )

    const showAdjudicatorDecisionData = () => (
        <>
        {props.data.map((row: AdjudicatorDecision, index: number) => {
            const isItemSelected = isSelected(row.uuid ?? "");
            return (
                <DataTableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.uuid}
                    selected={isItemSelected ?? false}
                    style={{backgroundColor: handleRowBackgroundColor(index)}}
                >
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.date} type="date" align={"left"}/>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.typeOfAttack} type="attack" align={"left"}/>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.decision} type="decision" align={"left"}/>
                </DataTableRow>
            )
        })}
        </>
    )

    const showAdjudicationSolvedData = () => (
        <>
        {props.data.map((row: AdjudicationSolved, index: number) => {
            const isItemSelected = isSelected(row.uuid ?? "");
            return (
                <DataTableRow
                    hover
                    aria-checked={isItemSelected}
                    tabIndex={-1}
                    key={row.uuid}
                    selected={isItemSelected ?? false}
                    style={{backgroundColor: handleRowBackgroundColor(index)}}
                >
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.decisionDate} type="date" align={"left"}/>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.issues} type="issue" align={"left"}/>
                    <DataTableCell onClick={() => props.onRowClick?.(row)} value={row.finalDecision} type="decision" align={"left"}/>
                </DataTableRow>
            )
        })}
        </>
    )
    
    const selectWhichTypeRepresent = () => {
        switch (props.type) {
            case "subjects":
                return showSubjectData();
            case "operations-face":
                return showOperationFaceData();
            case 'operations-fingers':
                return showOperationFingerData();
            case 'operations-voice':
                return showOperationVoiceData()
            case "accounts":
                return showAccountData()
            case "adjudication-pending":
                return showAdjudicationPendingData()
            case "adjudicator-decision":
                return showAdjudicatorDecisionData()
            case "adjudication-solved":
                return showAdjudicationSolvedData()
            default:
                enqueueSnackbar("Data table type not correct", {variant: "error"})
                break;
        }
    }

    return (          
        <TableContainer>
            <Table size="small">
                <EnhancedTableHead
                    numSelected={props.selected? props.selected.length : 0}
                    order={order}
                    orderBy={orderBy}
                    onSelectAllClick={handleSelectAllClick}
                    onRequestSort={handleRequestSort}
                    rowCount={props.data.length}
                    headCells={props.headCells}
                    inputProps={props.title.toLowerCase()}
                    disableUtilityCell={props.type === "accounts" || props.type === "adjudication-pending" || props.type === "adjudicator-decision" || props.type === "adjudication-solved"}
                    disableCheckBox={props.type === "accounts" || props.type === "adjudication-pending" || props.type === "adjudicator-decision" || props.type === "adjudication-solved"}
                />
                <TableBody>
                    {selectWhichTypeRepresent()}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default DataTable
